import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { useReplaceFrameContent } from 'hooks/queries/designs/useReplaceFrameContent'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentSlide } from '../CurrentSlideContext'
import { ConfirmReplaceDesignModal } from './ConfirmReplaceDesignModal'
import {
  ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { ActionButton } from './EditPostMenu'
import { useModals } from 'almanac-ui/Modal'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { Upload5 } from 'blaze-ui/icons/20/Upload5'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { isVideoChannel } from '../utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ImageMenuSelectedImage, Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { useState } from 'react'
import { usePollDesignReplacement } from './usePollDesignReplacement'

interface ReplaceDesignButtonProps {
  almanacFile: AlmanacFileShowItem
  onLoadingChange?: (isLoading: boolean) => void
}

export const ReplaceDesignButton = observer(({ almanacFile, onLoadingChange }: ReplaceDesignButtonProps) => {
  const [selectedMedia, setSelectedMedia] = useState<ImageMenuSelectedImage>()
  const { generationTimestampStore } = useRootStore()
  const { currentSlideIndex } = useCurrentSlide()
  const { openModal, closeStack } = useModals()

  const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(almanacFile.key)
  const isReplacingFrame = !!replaceFrameBatchId
  const isGenerating = almanacFile.is_generating || isReplacingFrame

  const isVideo = almanacFile?.channel ? isVideoChannel(almanacFile.channel as DesignChannel) : false

  const title = isVideo ? 'Replace this design with your own video' : 'Replace this design with your own image or video'
  const accept = isVideo ? ACCEPTED_VIDEO_MIME_TYPES : ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES_NO_SVG
  const hiddenProviders = isVideo
    ? ['gifs', 'blazeRecommendations', 'stockPhoto', 'hardcodedAi']
    : ['gifs', 'blazeRecommendations']

  const { mutate: replaceFrameContent } = useReplaceFrameContent(
    { designId: almanacFile.key },
    {
      onMutate: () => {
        onLoadingChange?.(true)
      },
      onSuccess: (data) => {
        generationTimestampStore.startGeneration(
          almanacFile.key,
          'default',
          'Replacing design...',
          selectedMedia?.thumbnail_url || selectedMedia?.url,
          data.batch_id,
        )
      },
      onError: () => {
        onLoadingChange?.(false)
        generationTimestampStore.completeGeneration(almanacFile.key)
        showToastMessage('Failed to replace design', 'error')
      },
    },
  )

  usePollDesignReplacement({
    replaceFrameBatchId,
    designKey: almanacFile.key,
    onComplete: () => {
      generationTimestampStore.completeGeneration(almanacFile.key)
      onLoadingChange?.(false)
    },
    scheduledPostId: almanacFile?.scheduled_post?.id,
  })

  return (
    <ActionButton
      frontIcon={Upload5}
      isDisabled={isGenerating}
      onPress={() => {
        openModal(
          ImageMenuModal,
          {
            title,
            hiddenProviders: hiddenProviders as Provider[],
            accept: accept,
            uploadMedia: true,
            maxCount: 1,
            closeOnUse: false,
            onUse: (images) => {
              const selectedMedia = images[0]
              setSelectedMedia(selectedMedia)
              openModal(ConfirmReplaceDesignModal, {
                title,
                mediaUrl: selectedMedia.url,
                mediaMimeType: selectedMedia.mime_type || '',
                onConfirm: () => {
                  if (!selectedMedia?.id) return

                  replaceFrameContent({
                    frames: [
                      {
                        frame_position: currentSlideIndex + 1,
                        media_file_key: selectedMedia.id,
                      },
                    ],
                  })
                  closeStack()
                },
              })
            },
          },
          { newStack: true },
        )
      }}
    >
      Replace design
    </ActionButton>
  )
})
